'use client';

import { useState } from 'react';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { MdChevronRight } from 'react-icons/md';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';
import type { TDynamicData } from '@eliadmin/core/src/types/dynamic-data';
import { FaFileLines } from 'react-icons/fa6';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@eliadmin/core/src/components/ui/select';

const categoryNames = ['Kategorie', 'Kategória', 'Category'];

export const DownloadContentDesktop = ({ data }: { data: TDynamicData[] }) => {
  const extractCategories = (data: TDynamicData[]): string[] => {
    const categoryNamesSet = new Set<string>();
    data.forEach(item => {
      item.fields.forEach(field => {
        if (categoryNames.includes(field.field.name)) {
          field.values.forEach(value => {
            categoryNamesSet.add(value.name);
          });
        }
      });
    });
    return Array.from(categoryNamesSet);
  };

  const categories = extractCategories(data);
  const [activeTab, setActiveTab] = useState<string>(categories[0] || '');

  const filterDataByCategory = (data: TDynamicData[], category: string): TDynamicData[] => {
    return data.filter(item => {
      return item.fields.some(field => {
        return (
          categoryNames.includes(field.field.name) &&
          field.values.some(value => value.name === category)
        );
      });
    });
  };

  const filteredData = filterDataByCategory(data, activeTab);

  return (
    <>
      <Tabs
        onValueChange={value => setActiveTab(value)}
        defaultValue={activeTab}
        className="hidden w-full items-start justify-start gap-10 md:flex"
      >
        <TabsList className="flex h-fit w-full max-w-[510px] flex-col gap-4 overflow-hidden rounded-lg bg-black/65 p-8 dark:bg-black/20">
          {categories?.map(category => (
            <TabsTrigger
              key={category}
              value={category}
              className={cn(
                'flex w-full items-center justify-between rounded-full px-8 py-4 font-medium text-white',
                'ea-download__tabs-trigger',
              )}
            >
              {category}
              <MdChevronRight />
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent
          value={activeTab}
          defaultValue={activeTab}
          className="flex w-full max-w-[600px] flex-col gap-8"
        >
          {filteredData.map(item => (
            <a
              href={item?.fields[1]?.file?.url}
              key={item?.name}
              className="flex items-center justify-start gap-3 text-primary"
              download
              target="_blank"
            >
              <FaFileLines size={32} />
              <p>{item?.name}</p>
            </a>
          ))}
        </TabsContent>
      </Tabs>
      <div
        className={cn('flex w-full flex-col items-start gap-8 md:hidden', 'ea-download__select')}
      >
        <Select onValueChange={value => setActiveTab(value)} defaultValue={activeTab}>
          <SelectTrigger className="flex w-full items-center justify-between rounded-full border border-[#333333] text-[#333333] dark:bg-transparent dark:text-white">
            <SelectValue placeholder={activeTab} />
          </SelectTrigger>
          <SelectContent>
            {categories.map(category => (
              <SelectItem key={category} value={category}>
                {category}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {filteredData.map(item => (
          <a
            href={item?.fields[1]?.file?.url}
            key={item?.name}
            className="flex items-center justify-start gap-3 text-primary"
            download
            target="_blank"
          >
            <FaFileLines size={32} />
            <p>{item?.name}</p>
          </a>
        ))}
      </div>
    </>
  );
};
